import { useEffect, useRef } from 'react';

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { useAuth } from '@/features/auth/context/auth-context';
import { can, useAccess } from '@/features/access/access';
import type { ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query/query-keys';

import { cvImportsApi } from '../api/cv-imports.api';
import type { CandidateMergeResult, CvUploadResult, CvImportFilters } from '../types';

/**
 * Dépôt d'un ou plusieurs CV.
 *
 * La requête se contente de déposer : elle répond 202 et rend la main tout de
 * suite. Le suivi et les notifications utilisent le journal persistant.
 */
export const useUploadCvs = (): UseMutationResult<CvUploadResult, ApiError, File[]> => {
  const client = useQueryClient();
  return useMutation<CvUploadResult, ApiError, File[]>({
    mutationKey: [...queryKeys.cvImports.all, 'upload'],
    mutationFn: (files) => cvImportsApi.upload(files),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.cvImports.all });
    },
  });
};

export const useCvImportSummary = () => {
  const { user } = useAuth();
  const access = useAccess();
  const client = useQueryClient();
  const query = useQuery({
    queryKey: [...queryKeys.cvImports.all, 'summary', user?.id],
    queryFn: cvImportsApi.summary,
    enabled: Boolean(user) && can(access.data, 'candidates'),
    refetchInterval: (result) =>
      result.state.data &&
      result.state.data.counts.PENDING + result.state.data.counts.PROCESSING > 0
        ? 3000
        : 15000,
    staleTime: 2000,
  });
  const previousCompleted = useRef<number | null>(null);
  const completed = query.data?.counts.COMPLETED;
  useEffect(() => {
    if (completed === undefined) return;
    if (previousCompleted.current !== null && previousCompleted.current !== completed) {
      void client.invalidateQueries({ queryKey: queryKeys.candidates.all });
      void client.invalidateQueries({ queryKey: [...queryKeys.cvImports.all, 'list'] });
    }
    previousCompleted.current = completed;
  }, [completed, client]);
  return query;
};

export const useCvImportHistory = (filters: CvImportFilters) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.cvImports.all, 'list', user?.id, filters],
    queryFn: () => cvImportsApi.list(filters),
    enabled: Boolean(user),
    refetchInterval: 5000,
  });
};

export const useReadCvImports = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: cvImportsApi.markRead,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.cvImports.all });
    },
  });
};

/**
 * Fusionne un doublon dans la fiche conservée.
 *
 * Invalide tout le domaine « candidats » : la fusion touche deux fiches et
 * modifie les listes (l'absorbée en disparaît). Cibler les clés une à une
 * laisserait immanquablement une vue périmée à l'écran.
 */
export const useMergeCandidates = (): UseMutationResult<
  CandidateMergeResult,
  ApiError,
  { targetId: string; duplicateId: string }
> => {
  const queryClient = useQueryClient();

  return useMutation<CandidateMergeResult, ApiError, { targetId: string; duplicateId: string }>({
    mutationFn: ({ targetId, duplicateId }) => cvImportsApi.merge(targetId, duplicateId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.candidates.all });
    },
  });
};

/**
 * Lève le signalement de doublon sans fusionner.
 *
 * La détection rapproche aussi sur le nom et le prénom : les homonymes
 * existent, et il faut pouvoir dire non.
 */
export const useDismissDuplicate = (): UseMutationResult<unknown, ApiError, string> => {
  const queryClient = useQueryClient();

  return useMutation<unknown, ApiError, string>({
    mutationFn: (id) => cvImportsApi.dismissDuplicate(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.candidates.all });
    },
  });
};
